"use client";

import { useState } from "react";
import { IconAlertTriangle } from "@tabler/icons-react";
import {
  Organization,
  useDeleteOrganization,
} from "@/features-root-user/service/organization";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/confirm-dialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentRow: Organization;
}

export function OrganizationsDeleteDialog({
  open,
  onOpenChange,
  currentRow,
}: Props) {
  const [value, setValue] = useState("");
  const deleteMutation = useDeleteOrganization();

  const handleDelete = () => {
    if (value.trim() !== currentRow.name) return;

    deleteMutation.mutate(currentRow.id, {
      onSuccess: () => {
        toast.success("Organization deleted", {
          description: (
            <pre className="bg-card-foreground/10 dark:bg-card mt-2 w-[340px] rounded-md p-4">
              <code className="text-foreground">
                {JSON.stringify(currentRow, null, 2)}
              </code>
            </pre>
          ),
        });
      },
      onError: (err: unknown) => {
        toast.error("Failed to delete organization", {
          description: String(err),
        });
      },
    });

    onOpenChange(false);
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      handleConfirm={handleDelete}
      disabled={value.trim() !== currentRow.name}
      title={
        <span className="text-destructive">
          <IconAlertTriangle
            className="stroke-destructive mr-1 inline-block"
            size={18}
          />{" "}
          Delete Organization
        </span>
      }
      desc={
        <div className="space-y-4">
          <p className="mb-2">
            Are you sure you want to delete{" "}
            <span className="font-bold">{currentRow.name}</span>?<br />
            This action will permanently remove the organization from the
            system. This cannot be undone.
          </p>

          <Label className="my-2">
            Organization Name:
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter organization name to confirm deletion."
            />
          </Label>

          <Alert variant="destructive">
            <AlertTitle>Warning!</AlertTitle>
            <AlertDescription>
              Please be careful, this operation cannot be rolled back.
            </AlertDescription>
          </Alert>
        </div>
      }
      confirmText="Delete"
      destructive
    />
  );
}
